import { z } from 'zod';

/**
 * Payment Processing Fee Config (PC721) as the reference has it (product inventory 13.15 and
 * the walk in docs/telgoo5-reference/modules/12-tax-and-fees.md): a fee for every way of paying
 * ("All") or for one payment option and category ("Individual"), either one amount ("Fixed")
 * or one amount per range of the charge ("Range"), each amount Flat or a Percentage.
 */

export const FEE_PAYMENT_OPTIONS = [
  'Credit Card',
  'Cash',
  'Money Order',
  'Money Gram',
  'Wallet',
  'Dealer Wallet',
  'Echeck',
] as const;
export type FeePaymentOption = (typeof FEE_PAYMENT_OPTIONS)[number];

/** The categories each payment option offers, as the reference's dialog lists them. */
export const FEE_CATEGORIES_BY_OPTION: Record<FeePaymentOption, readonly string[]> = {
  'Credit Card': [
    'Topup',
    'Data Topup',
    'Upgrade Topup',
    'Plan Change',
    'Plan Renew',
    'Equipment Purchase',
    'Autopay',
    'New Signup',
    'Bill Pay',
    'Add Wallet',
    'Reconnect',
  ],
  Cash: [
    'Topup',
    'Data Topup',
    'Upgrade Topup',
    'Plan Change',
    'Plan Renew',
    'Equipment Purchase',
    'New Signup',
    'Bill Pay',
    'Add Wallet',
    'Reconnect',
  ],
  'Money Order': [
    'Topup',
    'Data Topup',
    'Upgrade Topup',
    'Plan Change',
    'Plan Renew',
    'Equipment Purchase',
    'New Signup',
    'Bill Pay',
    'Reconnect',
  ],
  'Money Gram': [
    'Topup',
    'Data Topup',
    'Upgrade Topup',
    'Plan Change',
    'Plan Renew',
    'Equipment Purchase',
    'New Signup',
    'Bill Pay',
    'Reconnect',
  ],
  Wallet: [
    'Topup',
    'Data Topup',
    'Upgrade Topup',
    'Plan Change',
    'Plan Renew',
    'Equipment Purchase',
    'New Signup',
    'Bill Pay',
    'Reconnect',
  ],
  'Dealer Wallet': [
    'Topup',
    'Data Topup',
    'Upgrade Topup',
    'Plan Change',
    'Plan Renew',
    'Equipment Purchase',
    'New Signup',
    'Bill Pay',
    'Reconnect',
  ],
  Echeck: ['Bill Pay'],
};
/** The list's "Payment Category" filter. */
export const FEE_FILTER_CATEGORIES = [
  'Topup',
  'Data Topup',
  'Upgrade Topup',
  'Plan Change',
  'Plan Renew',
  'Equipment Purchase',
  'Autopay',
  'New Signup',
  'Bill Pay',
  'Add Wallet',
] as const;

export const FEE_SETUPS = ['All', 'Individual'] as const;
export const FEE_TYPES = ['Fixed', 'Range'] as const;
export const FEE_AMOUNT_TYPES = ['Flat', 'Percentage'] as const;
export type FeeAmountType = (typeof FEE_AMOUNT_TYPES)[number];

/** An amount of the dialog: cents when Flat, hundredths of a percent when Percentage. */
const amount = z
  .number({ invalid_type_error: 'Amount should not be blank.' })
  .int()
  .min(0, 'Amount should not be blank.')
  .max(99_999, 'Amount should not be blank.');

export const feeRangeSchema = z.object({
  /** "Provide Range" in whole dollars, at most four digits: from (read-only) < to. */
  fromDollars: z.number().int().min(0).max(9999),
  toDollars: z.number().int().min(1).max(9999),
  amount,
  amountType: z.enum(FEE_AMOUNT_TYPES),
});
export type FeeRange = z.infer<typeof feeRangeSchema>;

export const processingFeeSchema = z
  .object({
    setup: z.enum(FEE_SETUPS, {
      errorMap: () => ({ message: 'Please select Processing Fee Setup.' }),
    }),
    paymentOption: z.union([z.enum(FEE_PAYMENT_OPTIONS), z.literal('')]).default(''),
    paymentCategory: z.string().trim().max(40).default(''),
    feeType: z.enum(FEE_TYPES).default('Fixed'),
    amount: amount.optional(),
    amountType: z.enum(FEE_AMOUNT_TYPES).default('Flat'),
    ranges: z.array(feeRangeSchema).max(20).default([]),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (v.setup === 'Individual') {
      if (!v.paymentOption) issue('paymentOption', 'Please select Payment Option.');
      else if (
        v.paymentCategory !== 'All' &&
        !FEE_CATEGORIES_BY_OPTION[v.paymentOption].includes(v.paymentCategory)
      )
        issue('paymentCategory', 'Please select Payment Category.');
    }
    const percentOk = (a: number, t: FeeAmountType) => t !== 'Percentage' || a <= 10_000;
    if (v.feeType === 'Fixed') {
      if (v.amount === undefined) issue('amount', 'Amount should not be blank.');
      else if (!percentOk(v.amount, v.amountType))
        issue('amount', 'Value should be less then or equal to 100.');
      return;
    }
    if (v.ranges.length === 0) issue('ranges', 'Please input the range in Provide Range.');
    let from = 0;
    v.ranges.forEach((r, i) => {
      if (r.fromDollars !== from || r.toDollars <= r.fromDollars)
        issue('ranges', `Invalid Range. (row ${i + 1})`);
      if (!percentOk(r.amount, r.amountType))
        issue('ranges', `Value should be less then or equal to 100. (row ${i + 1})`);
      from = r.toDollars;
    });
  });
export type ProcessingFeeInput = z.input<typeof processingFeeSchema>;

export const processingFeeQuerySchema = z.object({
  paymentType: z.string().trim().max(40).optional(),
  paymentCategory: z.string().trim().max(40).optional(),
});

export interface ProcessingFeeDto {
  id: string;
  setup: (typeof FEE_SETUPS)[number];
  /** '' for an "All" fee. */
  paymentOption: FeePaymentOption | '';
  /** '' for an "All" fee; `All` or a category for an Individual one. */
  paymentCategory: string;
  feeType: (typeof FEE_TYPES)[number];
  amount: number;
  amountType: FeeAmountType;
  ranges: FeeRange[];
  createdAt: string;
  updatedAt: string;
}

/** "$1.50" or "2.5%", as a fee amount reads. */
export const feeAmountLabel = (value: number, type: FeeAmountType): string =>
  type === 'Percentage'
    ? `${(value / 100).toFixed(2).replace(/\.?0+$/, '')}%`
    : `$${(value / 100).toFixed(2)}`;
